/**
 * Margin Health Widget — Admin only
 * Shows real-time MTD revenue/cost/margin per tier with color-coded status.
 * © 2026 A-1 Affiliate Marketing LLC
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MarginHealthWidget.css';

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const STATUS_COLOR = {
  healthy: '#1f8a4c',
  warn: '#c98a08',
  alert: '#c93838',
  no_data: '#6b6b6b',
};

const STATUS_LABEL = {
  healthy: 'Healthy',
  warn: 'Watch',
  alert: 'Below floor',
  no_data: '—',
};

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export const MarginHealthWidget = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/admin/margin-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load margin health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 60_000); // refresh every 60s
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <div className="margin-health-widget" data-testid="margin-health-loading">
        <div className="mh-header">📈 Margin Health · loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="margin-health-widget" data-testid="margin-health-error">
        <div className="mh-header">📈 Margin Health</div>
        <div className="mh-error">{error}</div>
      </div>
    );
  }

  const { tiers, totals, margin_floor_pct } = data;

  return (
    <div className="margin-health-widget" data-testid="margin-health-widget">
      <div className="mh-header">
        <span>📈 Margin Health (MTD)</span>
        <span className="mh-floor">floor {margin_floor_pct}%</span>
      </div>
      <div className="mh-total" data-testid="margin-health-total">
        <span className="mh-total-label">Total</span>
        <span className="mh-total-rev">{fmt(totals.revenue)}</span>
        <span className="mh-total-margin">
          {totals.margin_pct !== null ? `${totals.margin_pct}% margin` : 'no revenue'}
        </span>
      </div>
      <table className="mh-table">
        <thead>
          <tr>
            <th>Tier</th>
            <th>Subs</th>
            <th>Rev</th>
            <th>Margin</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {tiers.map((t) => (
            <tr key={t.tier} data-testid={`margin-row-${t.tier}`}>
              <td className="mh-tier">{(t.tier || '').charAt(0).toUpperCase() + (t.tier || '').slice(1)}</td>
              <td>{t.subscribers}</td>
              <td>{fmt(t.revenue)}</td>
              <td>{t.margin_pct !== null ? `${t.margin_pct}%` : '—'}</td>
              <td>
                <span
                  className="mh-status-pill"
                  style={{ background: STATUS_COLOR[t.status] }}
                  data-testid={`margin-status-${t.tier}`}
                >
                  {STATUS_LABEL[t.status]}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MarginHealthWidget;
